/**
 * Map Operations Optimized Service
 * Provides optimized map operations with OpenStreetMap controller integration
 * Replaces direct Leaflet API calls with controller-based architecture
 */

import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { CROP_COLOR_MAP, FALLBACK_SEASON_COLORS } from '../constants/crop-colors';
import { MapControllerInput } from '@components/openstreetmap/interfaces/controller-input.interface';

export interface MapCoordinate {
  lat: number;
  lng: number;
}

export interface MapBounds {
  northEast: MapCoordinate;
  southWest: MapCoordinate;
}

export interface MarkerOptions {
  id: string;
  lat: number;
  lng: number;
  title?: string;
  icon?: string;
  color?: string;
  popup?: string;
  draggable?: boolean;
}

export interface PolygonOptions {
  id: string;
  coordinates: Array<[number, number]>;
  name?: string;
  fillColor?: string;
  strokeColor?: string;
  fillOpacity?: number;
  strokeWidth?: number;
  popup?: string;
}

export interface DrawingOptions {
  mode: 'point' | 'polygon' | 'freehand' | null;
  enabled: boolean;
  color?: string;
  fillOpacity?: number;
}

@Injectable({
  providedIn: 'root'
})
export class MapOperationsOptimizedService {
  // =========================================================================
  // STATE MANAGEMENT
  // =========================================================================

  private mapCenterSubject = new BehaviorSubject<MapCoordinate>({ lat: 41.3775, lng: 64.5853 });
  public mapCenter$ = this.mapCenterSubject.asObservable();

  private mapZoomSubject = new BehaviorSubject<number>(6);
  public mapZoom$ = this.mapZoomSubject.asObservable();

  private markersSubject = new BehaviorSubject<Map<string, MarkerOptions>>(new Map());
  public markers$ = this.markersSubject.asObservable();

  private polygonsSubject = new BehaviorSubject<Map<string, PolygonOptions>>(new Map());
  public polygons$ = this.polygonsSubject.asObservable();

  private drawingStateSubject = new BehaviorSubject<DrawingOptions>({
    mode: null,
    enabled: false
  });
  public drawingState$ = this.drawingStateSubject.asObservable();

  private visibleBoundsSubject = new BehaviorSubject<MapBounds | null>(null);
  public visibleBounds$ = this.visibleBoundsSubject.asObservable();

  private isMapMovingSubject = new BehaviorSubject<boolean>(false);
  public isMapMoving$ = this.isMapMovingSubject.asObservable();

  // =========================================================================
  // CONTROLLER COMMUNICATION
  // =========================================================================

  private mapControllerSubject = new Subject<MapControllerInput>();
  public mapController$ = this.mapControllerSubject.asObservable();

  constructor(private translate: TranslateService) {}

  // =========================================================================
  // MAP CENTER & ZOOM OPERATIONS
  // =========================================================================

  /**
   * Set map center and zoom
   */
  setMapCenter(lat: number, lng: number, zoom?: number, animate: boolean = true): void {
    const coordinate: MapCoordinate = { lat, lng };
    this.mapCenterSubject.next(coordinate);

    if (zoom !== undefined) {
      this.mapZoomSubject.next(zoom);
    }

    this.mapControllerSubject.next({
      setCenter: { lat, lng, zoom }
    });
  }

  /**
   * Get current map center
   */
  getMapCenter(): MapCoordinate {
    return this.mapCenterSubject.value;
  }

  /**
   * Set map zoom level
   */
  setZoom(zoomLevel: number): void {
    this.mapZoomSubject.next(zoomLevel);
    this.mapControllerSubject.next({
      setZoom: zoomLevel
    });
  }

  /**
   * Get current zoom level
   */
  getZoom(): number {
    return this.mapZoomSubject.value;
  }

  /**
   * Fit map bounds to show all markers/polygons
   */
  fitBounds(bounds: MapBounds | Array<[number, number]>, padding: number = 50): void {
    // Note: fitBounds expects Marker[] in MapControllerInput, need to convert or handle differently
    // For now, we'll use setCenter to approximate
    if (Array.isArray(bounds)) {
      const calculatedBounds = this.calculateBoundsFromPoints(bounds);
      const center = {
        lat: (calculatedBounds.northEast.lat + calculatedBounds.southWest.lat) / 2,
        lng: (calculatedBounds.northEast.lng + calculatedBounds.southWest.lng) / 2
      };
      this.setMapCenter(center.lat, center.lng);
    }
  }

  /**
   * Zoom to a specific location
   */
  zoomToLocation(lat: number, lng: number, zoomLevel: number = 12): void {
    this.setMapCenter(lat, lng, zoomLevel);
  }

  /**
   * Update visible map bounds
   */
  updateVisibleBounds(bounds: MapBounds): void {
    this.visibleBoundsSubject.next(bounds);
  }

  /**
   * Get current visible bounds
   */
  getVisibleBounds(): MapBounds | null {
    return this.visibleBoundsSubject.value;
  }

  // =========================================================================
  // MARKER OPERATIONS
  // =========================================================================

  /**
   * Add or update a marker
   */
  addMarker(options: MarkerOptions): void {
    const markers = new Map(this.markersSubject.value);
    markers.set(options.id, options);
    this.markersSubject.next(markers);

    this.mapControllerSubject.next({
      addMarker: options as any
    });
  }

  /**
   * Add multiple markers
   */
  addMarkers(markersList: MarkerOptions[]): void {
    const markers = new Map(this.markersSubject.value);
    markersList.forEach(marker => markers.set(marker.id, marker));
    this.markersSubject.next(markers);

    this.mapControllerSubject.next({
      addMarkers: markersList as any[]
    });
  }

  /**
   * Remove marker by ID
   */
  removeMarker(markerId: string): void {
    const markers = new Map(this.markersSubject.value);
    markers.delete(markerId);
    this.markersSubject.next(markers);

    this.mapControllerSubject.next({
      removeMarker: { id: markerId }
    });
  }

  /**
   * Clear all markers
   */
  clearMarkers(): void {
    this.markersSubject.next(new Map());
    this.mapControllerSubject.next({
      clearMarkers: true
    });
  }

  /**
   * Get marker by ID
   */
  getMarker(markerId: string): MarkerOptions | undefined {
    return this.markersSubject.value.get(markerId);
  }

  /**
   * Get all markers
   */
  getMarkers(): MarkerOptions[] {
    return Array.from(this.markersSubject.value.values());
  }

  /**
   * Update marker appearance
   */
  updateMarkerStyle(markerId: string, style: Partial<MarkerOptions>): void {
    const marker = this.getMarker(markerId);
    if (marker) {
      const updated = { ...marker, ...style };
      this.addMarker(updated);
    }
  }

  // =========================================================================
  // POLYGON OPERATIONS
  // =========================================================================

  /**
   * Add or update a polygon
   */
  addPolygon(options: PolygonOptions): void {
    const polygons = new Map(this.polygonsSubject.value);
    polygons.set(options.id, options);
    this.polygonsSubject.next(polygons);

    this.mapControllerSubject.next({
      addPolygon: {
        coordinates: options.coordinates,
        properties: {
          id: options.id,
          name: options.name,
          fillColor: options.fillColor,
          strokeColor: options.strokeColor,
          fillOpacity: options.fillOpacity,
          strokeWidth: options.strokeWidth,
          popup: options.popup
        }
      }
    });
  }

  /**
   * Add multiple polygons
   */
  addPolygons(polygonsList: PolygonOptions[]): void {
    const polygons = new Map(this.polygonsSubject.value);
    polygonsList.forEach(poly => polygons.set(poly.id, poly));
    this.polygonsSubject.next(polygons);

    this.mapControllerSubject.next({
      addPolygons: polygonsList.map(poly => ({
        coordinates: poly.coordinates,
        properties: {
          id: poly.id,
          name: poly.name,
          fillColor: poly.fillColor,
          strokeColor: poly.strokeColor,
          fillOpacity: poly.fillOpacity,
          strokeWidth: poly.strokeWidth,
          popup: poly.popup
        }
      }))
    });
  }

  /**
   * Remove polygon by ID
   */
  removePolygon(polygonId: string): void {
    const polygons = new Map(this.polygonsSubject.value);
    polygons.delete(polygonId);
    this.polygonsSubject.next(polygons);

    // Note: removePolygon not in MapControllerInput interface
    // Just update local state for now
  }

  /**
   * Clear all polygons
   */
  clearPolygons(): void {
    this.polygonsSubject.next(new Map());
    this.mapControllerSubject.next({
      clearPolygons: true
    });
  }

  /**
   * Clear specific polygon types
   */
  clearPolygonsByType(type: 'region' | 'field' | 'season' | 'monitoring'): void {
    const polygons = new Map(this.polygonsSubject.value);
    const keysToDelete: string[] = [];

    polygons.forEach((poly, id) => {
      if (id.includes(type)) {
        keysToDelete.push(id);
      }
    });

    keysToDelete.forEach(id => polygons.delete(id));
    this.polygonsSubject.next(polygons);

    // Note: clearPolygonsByType not in MapControllerInput interface
    // Just update local state for now
  }

  /**
   * Get polygon by ID
   */
  getPolygon(polygonId: string): PolygonOptions | undefined {
    return this.polygonsSubject.value.get(polygonId);
  }

  /**
   * Get all polygons
   */
  getPolygons(): PolygonOptions[] {
    return Array.from(this.polygonsSubject.value.values());
  }

  /**
   * Update polygon style
   */
  updatePolygonStyle(polygonId: string, style: Partial<PolygonOptions>): void {
    const polygon = this.getPolygon(polygonId);
    if (polygon) {
      const updated = { ...polygon, ...style };
      this.addPolygon(updated);
    }
  }

  // =========================================================================
  // DRAWING OPERATIONS
  // =========================================================================

  /**
   * Enable drawing mode
   */
  enableDrawing(mode: 'point' | 'polygon' | 'freehand', options?: Partial<DrawingOptions>): void {
    const drawingState: DrawingOptions = {
      mode,
      enabled: true,
      ...options
    };
    this.drawingStateSubject.next(drawingState);

    // Map to MapControllerInput drawing modes
    if (mode === 'polygon') {
      // Polygon drawing is handled by the map component directly
    }
  }

  /**
   * Disable drawing mode
   */
  disableDrawing(): void {
    const drawingState: DrawingOptions = {
      mode: null,
      enabled: false
    };
    this.drawingStateSubject.next(drawingState);

    this.mapControllerSubject.next({
      cancelDrawing: true
    });
  }

  /**
   * Get current drawing state
   */
  getDrawingState(): DrawingOptions {
    return this.drawingStateSubject.value;
  }

  /**
   * Is drawing enabled
   */
  isDrawingEnabled(): boolean {
    return this.drawingStateSubject.value.enabled;
  }

  // =========================================================================
  // LAYER OPERATIONS
  // =========================================================================

  /**
   * Set base map layer
   */
  setBaseMap(mapType: 'osm' | 'landsat' | 'sentinel'): void {
    this.mapControllerSubject.next({
      setBaseMap: mapType
    });
  }

  /**
   * Set layer visibility
   */
  setLayerVisibility(layerName: string, visible: boolean): void {
    // Note: setLayerVisibility not in MapControllerInput interface
    // Handle with opacity instead
    this.setLayerOpacity(layerName, visible ? 1 : 0);
  }

  /**
   * Set layer opacity
   */
  setLayerOpacity(layerName: string, opacity: number): void {
    const clampedOpacity = Math.max(0, Math.min(1, opacity));

    // Use appropriate MapControllerInput properties
    if (layerName === 'base') {
      this.mapControllerSubject.next({
        setBaseLayerOpacity: clampedOpacity
      });
    } else if (layerName === 'osm') {
      this.mapControllerSubject.next({
        setOSMOverlayOpacity: clampedOpacity
      });
    }
  }

  // =========================================================================
  // UTILITY OPERATIONS
  // =========================================================================

  /**
   * Set map moving state
   */
  setMapMoving(isMoving: boolean): void {
    this.isMapMovingSubject.next(isMoving);
  }

  /**
   * Calculate bounds from array of coordinates
   */
  calculateBoundsFromPoints(points: Array<[number, number]>): MapBounds {
    if (points.length === 0) {
      return {
        northEast: { lat: 0, lng: 0 },
        southWest: { lat: 0, lng: 0 }
      };
    }

    let maxLat = points[0][0];
    let minLat = points[0][0];
    let maxLng = points[0][1];
    let minLng = points[0][1];

    points.forEach(([lat, lng]) => {
      maxLat = Math.max(maxLat, lat);
      minLat = Math.min(minLat, lat);
      maxLng = Math.max(maxLng, lng);
      minLng = Math.min(minLng, lng);
    });

    return {
      northEast: { lat: maxLat, lng: maxLng },
      southWest: { lat: minLat, lng: minLng }
    };
  }

  /**
   * Calculate distance between two coordinates (in km)
   */
  calculateDistance(coord1: MapCoordinate, coord2: MapCoordinate): number {
    const R = 6371; // Earth's radius in km
    const lat1Rad = (coord1.lat * Math.PI) / 180;
    const lat2Rad = (coord2.lat * Math.PI) / 180;
    const deltaLat = ((coord2.lat - coord1.lat) * Math.PI) / 180;
    const deltaLng = ((coord2.lng - coord1.lng) * Math.PI) / 180;

    const a =
      Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Get color for crop type
   */
  getCropColor(cropName: string): string {
    return CROP_COLOR_MAP[cropName] || '#3388ff';
  }

  /**
   * Get season color
   */
  getSeasonColor(season: string): string {
    const seasonColors: Record<string, string> = FALLBACK_SEASON_COLORS as any;
    return seasonColors[season] || '#3388ff';
  }

  /**
   * Pan map to coordinate
   */
  panTo(lat: number, lng: number): void {
    this.setMapCenter(lat, lng);
  }

  /**
   * Animate to location
   */
  flyTo(lat: number, lng: number, zoom: number = 12, duration: number = 1000): void {
    this.setMapCenter(lat, lng, zoom);
  }

  /**
   * Reset map to default view
   */
  resetMap(lat: number = 41.3775, lng: number = 64.5853, zoom: number = 6): void {
    this.clearMarkers();
    this.clearPolygons();
    this.disableDrawing();
    this.setMapCenter(lat, lng, zoom);
  }

  /**
   * Show popup at location
   */
  showPopup(lat: number, lng: number, content: string): void {
    // Note: showPopup not directly in MapControllerInput
    // This would need to be handled via marker popups
  }

  /**
   * Close all popups
   */
  closePopups(): void {
    // Note: closePopups not in MapControllerInput interface
    // Handled by map component internally
  }

  /**
   * Take screenshot of map
   */
  captureMapScreenshot(): Observable<Blob> {
    return new Observable(observer => {
      // Note: captureScreenshot not in MapControllerInput interface
      // Would need to be implemented separately
      observer.complete();
    });
  }

  /**
   * Get GeoJSON representation of all shapes
   */
  exportToGeoJSON(): any {
    const features: any[] = [];

    // Add polygons
    this.getPolygons().forEach(poly => {
      features.push({
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [poly.coordinates]
        },
        properties: {
          id: poly.id,
          name: poly.name,
          fillColor: poly.fillColor
        }
      });
    });

    // Add markers as points
    this.getMarkers().forEach(marker => {
      features.push({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [marker.lng, marker.lat]
        },
        properties: {
          id: marker.id,
          title: marker.title
        }
      });
    });

    return {
      type: 'FeatureCollection',
      features
    };
  }

  /**
   * Import GeoJSON
   */
  importFromGeoJSON(geoJSON: any): void {
    geoJSON.features?.forEach((feature: any) => {
      if (feature.geometry.type === 'Polygon') {
        const coords = feature.geometry.coordinates[0];
        this.addPolygon({
          id: feature.properties.id,
          name: feature.properties.name,
          coordinates: coords,
          fillColor: feature.properties.fillColor
        });
      } else if (feature.geometry.type === 'Point') {
        const [lng, lat] = feature.geometry.coordinates;
        this.addMarker({
          id: feature.properties.id,
          lat,
          lng,
          title: feature.properties.title
        });
      }
    });
  }
}
